import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const LocationContext = createContext();

const LOCATION_STORAGE_KEY = 'panchi_user_location';
const LOCATION_EXPIRY_HOURS = 24; // Re-detect after 24 hours

export function LocationProvider({ children }) {
  const [location, setLocation] = useState({
    city: '',
    country: 'India',  // Default to India for better demo
    countryCode: 'IN',
    currency: 'INR',  // Default to INR
    lat: null,
    lng: null,
    loading: true,
    detected: false,
  });

  // Invalid server-side locations that shouldn't be used as origin
  const invalidLocations = ['council bluffs', 'ashburn', 'boardman', 'the dalles'];
  
  const isValidCity = useCallback((city) => {
    if (!city) return false;
    return !invalidLocations.includes(city.toLowerCase());
  }, []);

  // Load cached location from localStorage
  const loadCachedLocation = useCallback(() => {
    try {
      const cached = localStorage.getItem(LOCATION_STORAGE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const hoursSinceCache = (Date.now() - timestamp) / (1000 * 60 * 60);
        
        // Use cached location if it's less than 24 hours old
        if (hoursSinceCache < LOCATION_EXPIRY_HOURS && data.detected) {
          return data;
        }
      }
    } catch (error) {
      console.error('Error loading cached location:', error);
    }
    return null;
  }, []);

  // Save location to localStorage
  const saveLocationToCache = useCallback((locationData) => {
    try {
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({
        data: locationData,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error saving location to cache:', error);
    }
  }, []);

  const detectLocation = useCallback(async (forceRefresh = false) => {
    // Check for cached location first (unless forcing refresh)
    if (!forceRefresh) {
      const cachedLocation = loadCachedLocation();
      if (cachedLocation) {
        setLocation({ ...cachedLocation, loading: false });
        return;
      }
    }

    try {
      // Try API-based detection first (more accurate)
      const geoData = await api.detectLocation();
      
      if (geoData.success) {
        // Filter out invalid server-side locations
        const detectedCity = geoData.city || '';
        const validCity = isValidCity(detectedCity) ? detectedCity : '';
        
        // Check if this looks like a data center location
        const isDataCenterLocation = !validCity && 
          ['Council Bluffs', 'Ashburn', 'Boardman', 'The Dalles'].some(
            dc => (geoData.city || '').includes(dc) || (geoData.region || '').includes(dc)
          );
        
        // If it's a data center location, default to India/INR for this India-first app
        if (isDataCenterLocation || geoData.country === 'United States' && !validCity) {
          const defaultLocation = {
            city: '',
            country: 'India',
            countryCode: 'IN',
            currency: 'INR',
            lat: null,
            lng: null,
            timezone: 'Asia/Kolkata',
            loading: false,
            detected: true,  // Mark as detected so we don't re-check
          };
          setLocation(defaultLocation);
          saveLocationToCache(defaultLocation); // Cache even default location
          return;
        }
        
        const newLocation = {
          city: validCity,
          country: geoData.country || 'India',
          countryCode: geoData.country_code || 'IN',
          currency: geoData.currency || 'INR',
          lat: geoData.lat,
          lng: geoData.lng,
          timezone: geoData.timezone,
          loading: false,
          detected: true,
        };
        setLocation(newLocation);
        saveLocationToCache(newLocation);
        return;
      }
    } catch (error) {
      console.error('API location detection failed:', error);
    }

    // Fallback to browser geolocation - only if no cached location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          // Try reverse geocoding
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();
            
            const countryCode = data.address?.country_code?.toUpperCase() || '';
            const currencyMap = {
              IN: 'INR', US: 'USD', GB: 'GBP', EU: 'EUR', JP: 'JPY',
              AU: 'AUD', CA: 'CAD', SG: 'SGD', AE: 'AED', TH: 'THB',
            };
            
            const detectedCity = data.address?.city || data.address?.town || data.address?.village || '';
            const validCity = isValidCity(detectedCity) ? detectedCity : '';
            
            const newLocation = {
              city: validCity,
              country: data.address?.country || '',
              countryCode,
              currency: currencyMap[countryCode] || 'USD',
              lat: latitude,
              lng: longitude,
              loading: false,
              detected: true,
            };
            setLocation(newLocation);
            saveLocationToCache(newLocation);
          } catch (error) {
            const partialLocation = {
              ...location,
              lat: latitude,
              lng: longitude,
              loading: false,
              detected: true,
            };
            setLocation(partialLocation);
            saveLocationToCache(partialLocation);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocation(prev => ({ ...prev, loading: false }));
        },
        { timeout: 10000 }
      );
    } else {
      setLocation(prev => ({ ...prev, loading: false }));
    }
  }, [isValidCity, loadCachedLocation, saveLocationToCache]);

  useEffect(() => {
    detectLocation();
  }, [detectLocation]);

  const updateCity = useCallback((city) => {
    setLocation(prev => {
      const updated = { ...prev, city };
      saveLocationToCache(updated);
      return updated;
    });
  }, [saveLocationToCache]);

  const updateCurrency = useCallback((currency) => {
    setLocation(prev => {
      const updated = { ...prev, currency };
      saveLocationToCache(updated);
      return updated;
    });
  }, [saveLocationToCache]);

  const formatCurrency = useCallback((amount, customCurrency = null) => {
    const curr = customCurrency || location.currency;
    const symbols = {
      USD: '$', INR: '₹', EUR: '€', GBP: '£', JPY: '¥',
      AUD: 'A$', CAD: 'C$', SGD: 'S$', AED: 'د.إ', THB: '฿',
    };
    return `${symbols[curr] || curr}${Number(amount).toLocaleString()}`;
  }, [location.currency]);

  // Get currency symbol for current currency
  const getCurrencySymbol = useCallback(() => {
    const symbols = {
      USD: '$', INR: '₹', EUR: '€', GBP: '£', JPY: '¥',
      AUD: 'A$', CAD: 'C$', SGD: 'S$', AED: 'د.إ', THB: '฿',
    };
    return symbols[location.currency] || location.currency;
  }, [location.currency]);

  // Force refresh location (useful for manual refresh button)
  const refreshLocation = useCallback(() => {
    detectLocation(true);
  }, [detectLocation]);

  return (
    <LocationContext.Provider value={{
      ...location,
      currencySymbol: getCurrencySymbol(),
      updateCity,
      updateCurrency,
      formatCurrency,
      refreshLocation,
    }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}

export default LocationContext;
